import { FC, useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  IconSearch,
  IconExternalLink,
  IconEye,
  IconShip,
} from '@tabler/icons-react';
import useGetPartnerBoats from '../../../hooks/useGetPartnerBoats';
import { getErrorMessage } from '../../../utils/errorUtils';

const BoatsList: FC = () => {
  const navigate = useNavigate();
  const {
    boats,
    isLoading,
    isError,
    error: errorDetails,
  } = useGetPartnerBoats();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  // Debounce search query (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Filter boats based on debounced search query
  const filteredBoats = useMemo(() => {
    if (!debouncedSearchQuery.trim()) return boats;
    const query = debouncedSearchQuery.toLowerCase();
    return boats.filter(
      boat =>
        boat.name.toLowerCase().includes(query) ||
        boat.slug.toLowerCase().includes(query) ||
        boat.currency.toLowerCase().includes(query),
    );
  }, [boats, debouncedSearchQuery]);

  if (isLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <CircularProgress />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="mx-auto max-w-7xl">
        <Alert severity="error" className="mb-4">
          <div className="font-semibold">Error loading boats</div>
          <div className="mt-2 text-sm">{getErrorMessage(errorDetails)}</div>
        </Alert>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Boats</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage your boats and view their details
        </p>
      </div>

      {/* Search and Filter */}
      <div className="mb-6">
        <TextField
          fullWidth
          placeholder="Search boats by name, slug, or currency..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <IconSearch className="h-5 w-5 text-gray-400" />
                </InputAdornment>
              ),
            },
          }}
          className="bg-white"
        />
      </div>

      {filteredBoats.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-8 text-center">
          <IconShip className="mx-auto h-16 w-16 text-gray-400" />
          {boats.length === 0 ? (
            <>
              <p className="mt-4 text-gray-500">No boats found</p>
              <p className="mt-2 text-sm text-gray-400">
                Get started by adding your first boat
              </p>
            </>
          ) : (
            <>
              <p className="mt-4 text-gray-500">
                No boats match your search criteria
              </p>
              <p className="mt-2 text-sm text-gray-400">
                Try adjusting your search terms
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Boat Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Slug
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Currency
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {filteredBoats.map(boat => (
                <tr key={boat.boatId} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-6 py-4">
                    <div className="text-sm font-medium text-gray-900">
                      {boat.name}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <div className="text-sm text-gray-500">{boat.slug}</div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <div className="text-sm text-gray-500">{boat.currency}</div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() =>
                          navigate(`/partner/boats/${boat.boatId}`)
                        }
                        className="flex items-center gap-1 text-blue-600 hover:text-blue-900"
                        title="View Details"
                      >
                        <IconEye className="h-4 w-4" />
                        <span>Details</span>
                      </button>
                      <a
                        href={`/boat/${boat.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-green-600 hover:text-green-900"
                        title="View Public Page"
                      >
                        <IconExternalLink className="h-4 w-4" />
                        <span>Public</span>
                      </a>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BoatsList;
